import React, { useEffect, useState } from "react";
import AdminTable from "../../Components/AdminTable/AdminTable";
import Select from "react-select";
import { MDBBtn, MDBCheckbox, MDBIcon, MDBInput, MDBSpinner } from "mdb-react-ui-kit";
import ApiService from "../../Core/Service/ApiService";
import Moment from "react-moment";
import { useNavigate, useParams } from "react-router-dom";
import LoadingIndicator from "../../Components/LoadingIndicator/LoadingIndicator";
import BulkActionPanel, { BulkActions } from "../../Components/CMS/BulkActionPanel";
import { LondonDate } from "../../Utils/utils";
import Paginator from "../../Components/Paginator/Paginator";
import { CmsType, CmsTypeStr } from "../../Constants/constants";
import { useContext } from "react";
import CmsContext from "../../Context/CmsContext";
import './style.scss';

const CmsList = React.memo((props) => {
    const params = useParams();
    const cmsContext = useContext(CmsContext);
    const {currMainMenu, currSubMenu} = cmsContext;
    const {type} = params;
    const navigate = useNavigate();
    const [total, setTotal] = useState(0);
    const [rows, setRows] = useState([]);
    const perPageOptions = [
        {value: 10, label: '10'},
        {value: 20, label: '20'},
        {value: 50, label: '50'},
        {value: 100, label: '100'},
    ]
    const [formData, setFormData] = useState({
        type: '',
        pageNum: 1,
        perPage: 10,
        search: {},
        sort: {
            name: 'page_pos',
            dir: 'ASC'
        }
    })
    const [loading, setLoading] = useState(false);
    const [bulkActioning, setBulkActioning] = useState(false);

    useEffect(() => {
        setFormData({
            ...formData,
            type
        })
    }, [type]);
    
    useEffect(() => {
        getRows();
    }, [formData]);

    useEffect(() => {
        cmsContext.setBreadcrumb([
            {title: currMainMenu?.label ?? ''},
            {title: currSubMenu?.label ?? ''},
            {title: 'List'},
        ]);
    }, [type])

    const onAllItemSelected = (selected) => {
        setRows(rows.map(row => ({
            ...row,
            selected: selected
        })))
    }
    const onPageChanged = (pageNum) => {
        setFormData({
            ...formData,
            pageNum
        })
    }
    const onPerPageChanged = (opt) => {
        setFormData({
            ...formData,
            pageNum: 1,
            perPage: opt.value
        })
    }
    const handleSearch = (search) => {
        setFormData({
            ...formData,
            pageNum: 1,
            search: search
        })
    }
    const handleSort = (sort) => {
        setFormData({
            ...formData,
            pageNum: 1,
            sort: sort
        })
    }
    const getRows = () => {
        if(!formData.type) return;

        setLoading(true); 
        const data = {
            ...formData,
            search: JSON.stringify(formData.search),
            sort: JSON.stringify(formData.sort)
        }
        ApiService.get('/cms', data).then((response)=>{
            const curr_rows = [...rows];

            setTotal(response.data.total);
            setRows(response.data.rows?.map((row, index) => {
                const old_row = curr_rows?.find(r => r.id == row.id);
                return {
                    ...row,
                    data: row.data ? JSON.parse(row.data) : {},
                    selected: old_row?.selected ?? false,
                }
            }));
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const onActiveChange = (e, row) => {
        const data = {
            active: e.target.checked
        }

        const message = e.target.checked ? "Are you sure to activate?" : "Are you sure to deactivate?";
        if(window.confirm(message) == false) return;
        
        ApiService.post(`/cms/${row.id}/activate`, data).then(() => {
            getRows();
        })
    }
    const onAddBtnClicked = (e) => {
        navigate(`/cms/${type}/add`);
    }
    const gotoEdit = (row) => {
        navigate(`/cms/${type}/edit/${row.id}`);
    }
    const gotoItems = (row) => {
        navigate(`/cms_item/${row.id}/list`);
    }
    const handleCopy = (row) => {
        navigate(`/cms/${type}/copy/${row.id}`);
    }

    const handleDelete = (row) => {
        if(window.confirm("Are you sure to delete?") == false) return;
        ApiService.delete(`/cms/${row.id}`).then(() => {
            getRows();
        })
    }
    const onRowSelected = (e, row) => {
        setRows(rows.map(r => {
            if(r.id != row.id) return r;
            return {
                ...r,
                selected: e.target.checked
            }
        }))
    }
    const handleBulkAction = (bulkData) => {
        if(!bulkData.action) return;
        const selRows = rows?.filter(row => row.selected);
        if(selRows?.length > 0) {
            if(window.confirm('Are you sure to do this action?') == false) return;
            
            setBulkActioning(true);
            const data = {
                ...bulkData,
                ids: selRows.map(r => r.id)
            }
            ApiService.post(`/cms/bulkaction`, data).then(() => {
                getRows();
            })
            .finally(() => {
                setBulkActioning(false);
            })
        }
    }
    const moveUpDown = (row, dir) => {
        ApiService.get(`/cms/move/${row.id}/${dir}`).then(() => {
            getRows();
        })
    }

    const renderTbodyTr = (row, rowIndex) => {
        const now = LondonDate(new Date());
        const start_date = LondonDate(row.start_date);
        const end_date = LondonDate(row.end_date);
        let className = "cursor-pointer ";

        if(row.active == "1") {
            if(end_date < now) className += "expired";
            if(start_date > now) className += "not-started";
            if(start_date < now && now < end_date) className += "active";
        } else {
            className += "inactive";
        }

        return (
            <tr key={rowIndex} className={`${className}`}>
                { columns.filter(col => col.disable != 1).map((col, colIndex) => {
                    const el = renderTdCell(row, col.data ?? col.name, rowIndex, colIndex);
                    if(el) {
                        return <td key={colIndex}>{el}</td>
                    } else {
                        return <td key={colIndex}>{row[col.name]}</td>
                    }
                })}
            </tr>
        )
    }

    const renderTdCell = (row, colname, rowIndex, colIndex) => {
        switch(colname) {
            case 'select':
                return <div className="d-flex align-items-center"> 
                    <MDBCheckbox name='' checked={row.selected ?? false} onChange={e => onRowSelected(e, row)} />
                    <span className="mt-1">{row.id}</span>
                </div>
            case 'start_date':
                return <div className="" onClick={e=>gotoEdit(row)}>
                    <Moment format="DD/MM/YYYY HH:mm::ss">{LondonDate(row.start_date)}</Moment>
                </div>
            case 'end_date':
                return <div className="" onClick={e=>gotoEdit(row)}>
                    <Moment format="DD/MM/YYYY HH:mm:ss">{LondonDate(row.end_date)}</Moment>
                </div>
            case 'item_count':
                return <div className="">
                    <MDBBtn className="ms-2 edit-items py-1" color="success" 
                        onClick={() => gotoItems(row)} title="Edit Content"
                    >
                        Edit
                        <div className="item-count">{row.item_count}</div>
                    </MDBBtn>
                </div>
            case 'created_at':
                return <div className="" onClick={(e=>gotoEdit(row))}>
                    <Moment format="DD/MM/YYYY HH:mm:ss">{LondonDate(row.created_at)}</Moment>
                </div>
            case 'updated_at':
                return <div className="" onClick={e=>gotoEdit(row)}>
                    <Moment format="DD/MM/YYYY HH:mm:ss">{LondonDate(row.updated_at)}</Moment>
                </div>
            case 'active':
                return <MDBCheckbox checked={row.active == "1"} onChange={e=>onActiveChange(e, row)} />
            case 'action':
                return <div className="d-flex justify-content-end">
                <MDBBtn color="none" tag="a" onClick={() => gotoEdit(row)} title="Edit Content">
                    <MDBIcon fas icon="pencil-alt" size="lg" color="warning" />
                </MDBBtn>
                <MDBBtn color="none" tag="a" title="Copy Content" onClick={() => handleCopy(row)}>
                    <MDBIcon far icon='copy' size="lg" className="ms-2" color="warning" />
                </MDBBtn>
                <MDBBtn color="none" tag="a" onClick={()=>handleDelete(row)} title="Delete Content">
                    <MDBIcon far icon="trash-alt" size="lg" color="danger" className="ms-2" />
                </MDBBtn>
            </div>
            default:
                return <div className="" onClick={e=>gotoEdit(row)}>{row[colname]}</div>;
            
        }        
    }

    const columns = [
        {name: 'select', title: 'ID', headerStyle:{width:50, paddingLeft:30}, search: {
            enable: true, type: 'check', onChange: onAllItemSelected
        }},
        {name: 'start_date', title: 'Start Date', headerStyle:{width:250}, sortable: true, search: {
            enable: true, type: 'date', ops:[{value: '', label: 'All'}, {value: '<', label:'<'}, {value: '>=', label: '>='}]
        }},
        {name: 'end_date', title: 'End Date', headerStyle:{width:250}, sortable: true, search: {
            enable: true, type: 'date', ops:[{value: '', label: 'All'}, {value: '<', label:'<'}, {value: '>=', label: '>='}]
        }},
        {name: 'internal_name', title: 'Internal Name', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {data: 'organization', name: 'epos_organizations.name', title: 'Organization', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'branches', title: 'Branches', search: {
            enable: true, type: 'text', ops:[{value: 'like', label: 'Like'}]
        }},
        {name: 'page_pos', title: 'Page Pos', headerStyle:{width:100}, sortable: true, 
            disable: (type === CmsType.Category_Banner || type === CmsType.Top_Ribbon || type == CmsType.MultiBuy || type === CmsType.Splash_Screen)},
        {name: 'dwell_time', title: 'Dwell', headerStyle:{width:100}},
        {name: 'item_count', title: 'Items', headerStyle:{width:100},
            disable: [CmsType.Products_Carousel, CmsType.MultiBuy, CmsType.Top_Ribbon, CmsType.Sponsor, CmsType.Presell_Section, CmsType.TradeShow_Section, CmsType.Splash_Screen].includes(type)},
        {name: 'template_name', title: 'Template', headerStyle:{width:100},
            disable: (type == CmsType.MultiBuy || type == CmsType.Top_Ribbon)},
        {name: 'epos_managers.username', data: 'editor_username', title: 'Editor', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'active', title: 'Active', headerStyle:{width:100}, sortable: true, search: {
            enable: true, type: 'select', ops:[{value:'=', label: '='}], options:[{value: '', label: 'All'}, {value: '1', label: 'Active'}, {value: '0', label: 'Deactive'}]
        }},
        {name: 'action', title: 'Action', headerClassName: ""}
    ];

    return (<div className="d-flex flex-column h-100 cms-list">
        <div className="d-flex align-items-center me-1 mt-2">
            <div className="flex-fluid d-flex align-items-center ps-2">
                <label>Per Page:</label>
                <div className="ms-2" style={{width:100}}>
                    <Select 
                        options={perPageOptions} 
                        value={perPageOptions.find(opt=>opt.value == formData.perPage)} 
                        onChange={onPerPageChanged}
                    />
                </div>
                <label className="ms-4">
                    Found 
                    <span className='fw-bold mx-2' style={{color: '#333'}}>{total}</span> 
                    Matching Results.
                </label>
            </div>

            <div className="d-flex align-items-center">
                <BulkActionPanel 
                    bulkActions={[
                        BulkActions.Activate,
                        BulkActions.Deactivate,
                        BulkActions.Delete,
                        BulkActions.StartDate,
                        BulkActions.EndDate,
                        BulkActions.DwellTime,
                        BulkActions.PagePosition,
                    ]}
                    handleBulkAction={handleBulkAction} 
                    bulkActioning={bulkActioning} 
                />
                <MDBBtn className="ms-4" onClick={onAddBtnClicked}>
                    <MDBIcon icon="plus" />
                    <span className="ms-1">Create New Content</span>
                </MDBBtn>
            </div>
        </div>

        <div className="flex-fluid mt-3">
            <AdminTable 
                columns={columns} 
                rows={rows} 
                renderTbodyTr={renderTbodyTr} 
                handleSearch={handleSearch} 
                handleSort={handleSort}
                search={formData.search} 
                sort={formData.sort}
            />
        </div>

        <div className="flex-fluid d-flex justify-content-center mt-4">
            <Paginator curPage={formData.pageNum} total={total} perPage={formData.perPage} onPageChanged={onPageChanged} />
        </div>

        <LoadingIndicator show={loading} />
    </div>)
})

export default CmsList;